// src/services/stockTopUpForm.ts
// ------------------------------
// Pure helpers + hard-coded matrices for the Stock Top-up capture form (stock-top-ups
// plan §2). ISOLATED module — it imports nothing from the fit-out/request code, and the
// fit-out path imports nothing from here. Everything is pure so it's unit-testable; the
// StockTopUpStep component holds the state and calls buildTopUpLines / validateTopUp.

// ── Hard-coded matrices (NOT catalogue rows — plan §2) ───────────────────────────────
export const PACK_YEARS = ["Prep", "Y1", "Y2", "Y3/4", "Y5/6", "High School"] as const;
export const BAG_COLOURS = ["Black", "Burgundy", "Navy", "Royal", "Green"] as const;
export const BAG_LEVELS = ["Primary", "High"] as const;
export const HAT_COLOURS = BAG_COLOURS;
export const HAT_SIZES = ["X Small", "Small", "Medium", "Large"] as const;
export const SOCK_SIZES = ["Small 9-12", "13-3", "2-8", "Large 7-11", "Large 11-14"] as const;

// Hard-validation limits (plan §2).
export const PACK_MAX = 3; // per year level
export const BAG_MIN = 20; // total if the section is used
export const HAT_MIN = 30;
export const SOCK_MIN = 36; // packs (each pack = 2 pairs). Was 18 until 19 Oct 2026 (David).
export const REIMBURSEMENT_AMOUNT = 150;

// The two Other-Stock quantity items (when NOT reimbursement).
export const OTHER_ITEMS = [
  { key: "lunch_box", label: "Lunch Boxes" },
  { key: "water_bottle", label: "Water Bottles" },
] as const;

// ── Section metadata + line labels (the Top-up approval view) ─────────────────────────
export const SECTION_ORDER = ["packs", "bulk", "bags", "other", "hats", "socks"] as const;
export const SECTION_LABELS: Record<string, string> = {
  packs: "Stationery Packs",
  bulk: "Bulk Stationery",
  bags: "School Bags",
  other: "Other Stock",
  hats: "Hats",
  socks: "Socks",
};

// Pack-unit label per section (John, 16 Sep 2026). Shown after the quantity on the picker
// breakdown, and beside the item on the reviewer + shelter views, so the ORDERING UNIT is
// unambiguous: Socks are ordered in 2-pair packs, Bulk Stationery in packs of 12. Every
// other section is a plain count and carries no label. DISPLAY-ONLY — this never touches a
// quantity, a sales-order line, the ordered amount, or pricing.
export const SECTION_UNIT_LABELS: Record<string, string> = {
  socks: "Pkts (2 pairs)",
  bulk: "Pkt (12 per Pkt)",
};

// The pack-unit label for a section, or "" when the section is a plain count.
export function sectionUnitLabel(section: string | null | undefined): string {
  return SECTION_UNIT_LABELS[String(section ?? "")] || "";
}

// Fields a stored line carries that the label reads (a subset of stock_top_up_lines).
export interface LineLabelFields {
  section: string;
  item_key: string;
  item_name?: string | null;
  colour?: string | null;
  size?: string | null;
  year_level?: string | null;
  level?: string | null;
}

// A human label for a stored line (the approval + read views): reads the dimension
// columns per section, falling back to item_name / item_key.
export function lineLabel(line: LineLabelFields): string {
  switch (line.section) {
    case "packs":
      return line.year_level || line.item_key;
    case "bulk":
      return line.item_name || line.item_key;
    case "bags":
      return [line.colour, line.level].filter(Boolean).join(" ") || line.item_key;
    case "hats":
      return [line.colour, line.size].filter(Boolean).join(" ") || line.item_key;
    case "socks":
      return line.size || line.item_key;
    default:
      return line.item_name || line.item_key;
  }
}

// Order captured lines the SAME way the request/capture form shows them (John, 12 Aug):
// sections in SECTION_ORDER, and WITHIN a section by the canonical order — Bulk Stationery
// by the catalogue (stock_items) order, the fixed matrices by their matrix order. Without
// this, bulk lines come back ordered by their stock_item_id (a random UUID), so the same
// item (e.g. the three Biros) ends up scattered — hard to review and hard to pick. Pure:
// `stockItems` must be in catalogue (sort_order) order.
export function sortTopUpLines<
  T extends {
    section: string;
    item_key?: string | null;
    stock_item_id?: string | null;
    year_level?: string | null;
    size?: string | null;
  },
>(lines: T[], stockItems: { id: string }[]): T[] {
  const bulkIndex = new Map(stockItems.map((it, i) => [it.id, i] as const));
  const rankIn = (arr: readonly string[], v: string | null | undefined): number => {
    const i = arr.indexOf(String(v ?? ""));
    return i === -1 ? 999 : i;
  };
  const rank = (l: T): number => {
    switch (l.section) {
      case "packs":
        return rankIn(PACK_YEARS, l.year_level ?? l.item_key);
      case "bulk":
        return bulkIndex.get(String(l.stock_item_id ?? l.item_key ?? "")) ?? 999;
      case "bags": {
        const [c, lv] = String(l.item_key ?? "").split("|");
        return rankIn(BAG_COLOURS, c) * 10 + rankIn(BAG_LEVELS, lv);
      }
      case "hats": {
        const [c, s] = String(l.item_key ?? "").split("|");
        return rankIn(HAT_COLOURS, c) * 10 + rankIn(HAT_SIZES, s);
      }
      case "socks":
        return rankIn(SOCK_SIZES, l.size ?? l.item_key);
      case "other":
        return l.item_key === "reimbursement" ? 2 : l.item_key === "water_bottle" ? 1 : 0;
      default:
        return 999;
    }
  };
  const sectionOrder = SECTION_ORDER as readonly string[];
  return [...lines].sort((a, b) => {
    const sa = sectionOrder.indexOf(a.section);
    const sb = sectionOrder.indexOf(b.section);
    if (sa !== sb) return sa - sb;
    return rank(a) - rank(b);
  });
}

// A single qty cell — strings because they come straight from number inputs.
export interface QtyCell {
  onHand: string;
  required: string;
}

export interface TopUpState {
  // Which of the six sections the service turned on (the top tabs). Only enabled
  // sections are shown, validated, and submitted.
  enabledSections: Record<string, boolean>;
  packs: Record<string, QtyCell>; // key = year level
  bulk: Record<string, QtyCell>; // key = stock_item_id
  bags: Record<string, QtyCell>; // key = `${colour}|${level}`
  otherMode: "quantities" | "reimbursement";
  other: Record<string, QtyCell>; // keys: lunch_box, water_bottle
  hats: Record<string, QtyCell>; // key = `${colour}|${size}`
  socks: Record<string, QtyCell>; // key = size
  note: string;
}

export interface StockItemLike {
  id: string;
  name: string;
  max_qty: number;
}

export function emptyTopUpState(): TopUpState {
  return {
    enabledSections: {},
    packs: {},
    bulk: {},
    bags: {},
    otherMode: "quantities",
    other: {},
    hats: {},
    socks: {},
    note: "",
  };
}

// Parse a positive integer from an input string (blank / 0 / invalid → 0).
function num(v: string | undefined): number {
  const n = Number.parseInt(v ?? "", 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function sectionTotal(cells: Record<string, QtyCell>): number {
  return Object.values(cells).reduce((sum, c) => sum + num(c?.required), 0);
}

export function bagsTotal(s: TopUpState): number {
  return sectionTotal(s.bags);
}
export function hatsTotal(s: TopUpState): number {
  return sectionTotal(s.hats);
}
export function socksTotal(s: TopUpState): number {
  return sectionTotal(s.socks);
}

// Is there anything to submit? Reimbursement counts on its own; otherwise any qty.
export function topUpHasAnything(s: TopUpState): boolean {
  const on = s.enabledSections;
  if (on.other && s.otherMode === "reimbursement") return true;
  let total = 0;
  if (on.packs) total += sectionTotal(s.packs);
  if (on.bulk) total += sectionTotal(s.bulk);
  if (on.bags) total += sectionTotal(s.bags);
  if (on.other) total += sectionTotal(s.other);
  if (on.hats) total += sectionTotal(s.hats);
  if (on.socks) total += sectionTotal(s.socks);
  return total > 0;
}

// A one-line summary of a top-up's contents, one entry per section in use, e.g.
// ["School Bags 20", "Socks 36 packs", "Other Stock: $150 reimbursement"]. Used where staff
// see a prepared top-up at a glance (no stock catalogue needed — bulk is a pack count).
export function topUpSummary(s: Partial<TopUpState> | null | undefined): string[] {
  if (!s) return [];
  const on = s.enabledSections || {};
  const out: string[] = [];
  for (const section of SECTION_ORDER) {
    if (!on[section]) continue;
    const label = SECTION_LABELS[section];
    if (section === "other" && s.otherMode === "reimbursement") {
      out.push(`${label}: $${REIMBURSEMENT_AMOUNT} reimbursement`);
      continue;
    }
    const cells = (s as Record<string, unknown>)[section] as Record<string, QtyCell> | undefined;
    const total = sectionTotal(cells || {});
    if (total <= 0) continue;
    out.push(section === "socks" ? `${label} ${total} packs` : `${label} ${total}`);
  }
  return out;
}

// Hard validation (plan §2). Returns human-readable error strings ([] = valid).
// Section minimums apply ONLY when a section is used (total > 0) — a top-up can skip
// any section, but if it orders from one it must meet that section's minimum.
//
// opts.requireOnHand (John, 5 Sep 2026): when true, every ordered item (Required > 0)
// must ALSO carry an "On hand" figure (0 is a valid count — only a BLANK cell fails).
// Applied to a DFV Service creating its own top-up; NOT to a staff-prepared prefill
// (staff often don't know the service's stock, and the review is read-only), so it
// defaults OFF and existing callers are unaffected.
export function validateTopUp(
  s: TopUpState,
  stockItems: StockItemLike[],
  opts: { requireOnHand?: boolean } = {}
): string[] {
  const errs: string[] = [];
  const on = s.enabledSections;

  if (on.packs) {
    for (const y of PACK_YEARS) {
      if (num(s.packs[y]?.required) > PACK_MAX) {
        errs.push(`Stationery Packs — ${y}: max ${PACK_MAX} per year level.`);
      }
    }
  }

  if (on.bulk) {
    const itemById = new Map(stockItems.map((i) => [i.id, i]));
    for (const [id, cell] of Object.entries(s.bulk)) {
      const item = itemById.get(id);
      if (item && num(cell.required) > item.max_qty) {
        errs.push(`Stationery Bulk — ${item.name}: max ${item.max_qty} packs.`);
      }
    }
  }

  if (on.bags) {
    const bt = bagsTotal(s);
    if (bt > 0 && bt < BAG_MIN) {
      errs.push(`School Bags: order at least ${BAG_MIN} in total (currently ${bt}).`);
    }
  }
  if (on.hats) {
    const ht = hatsTotal(s);
    if (ht > 0 && ht < HAT_MIN) {
      errs.push(`Hats: order at least ${HAT_MIN} in total (currently ${ht}).`);
    }
  }
  if (on.socks) {
    const st = socksTotal(s);
    if (st > 0 && st < SOCK_MIN) {
      errs.push(`Socks: order at least ${SOCK_MIN} packs in total (currently ${st}).`);
    }
  }

  // On-hand required for every ordered item (opt-in — shelter-created top-ups only).
  if (opts.requireOnHand) {
    // A cell is "missing on hand" when it's being ordered (Required > 0) but its
    // On-hand box is blank. "0" is a real stock count and passes.
    const blankOnHand = (cells: Record<string, QtyCell>): boolean =>
      Object.values(cells || {}).some(
        (c) => num(c?.required) > 0 && String(c?.onHand ?? "").trim() === ""
      );
    const sectionsToCheck: Array<[string, string, Record<string, QtyCell>]> = [
      ["packs", "Stationery Packs", s.packs],
      ["bulk", "Stationery Bulk", s.bulk],
      ["bags", "School Bags", s.bags],
      ["hats", "Hats", s.hats],
      ["socks", "Socks", s.socks],
    ];
    for (const [key, label, cells] of sectionsToCheck) {
      if (on[key] && blankOnHand(cells)) {
        errs.push(`${label}: enter the quantity on hand for every item you're ordering.`);
      }
    }
    // Other Stock only has items in "quantities" mode; the $150 reimbursement has none.
    if (on.other && s.otherMode === "quantities" && blankOnHand(s.other)) {
      errs.push("Other Stock: enter the quantity on hand for every item you're ordering.");
    }
  }

  if (!topUpHasAnything(s)) {
    errs.push("Add at least one item, or select the $150 reimbursement.");
  }
  return errs;
}

// A built line, matching the stock_top_up_lines column shape (db-155). Only filled
// lines are produced. qty_on_hand is informational (null when blank); amount is only
// the Other-Stock reimbursement.
export interface TopUpLine {
  section: string;
  item_key: string;
  item_name?: string;
  colour?: string;
  size?: string;
  year_level?: string;
  level?: string;
  stock_item_id?: string;
  qty_on_hand?: number | null;
  qty_required?: number | null;
  amount?: number | null;
}

export function buildTopUpLines(s: TopUpState, stockItems: StockItemLike[]): TopUpLine[] {
  const lines: TopUpLine[] = [];
  const onHand = (c?: QtyCell): number | null => {
    const n = num(c?.onHand);
    return n > 0 ? n : null;
  };

  for (const y of PACK_YEARS) {
    const c = s.packs[y];
    const r = num(c?.required);
    if (r > 0) {
      lines.push({
        section: "packs",
        item_key: y,
        year_level: y,
        qty_on_hand: onHand(c),
        qty_required: r,
      });
    }
  }

  const nameById = new Map(stockItems.map((i) => [i.id, i.name]));
  for (const [id, c] of Object.entries(s.bulk)) {
    const r = num(c.required);
    if (r > 0) {
      lines.push({
        section: "bulk",
        item_key: id,
        item_name: nameById.get(id),
        stock_item_id: id,
        qty_on_hand: onHand(c),
        qty_required: r,
      });
    }
  }

  for (const colour of BAG_COLOURS) {
    for (const level of BAG_LEVELS) {
      const key = `${colour}|${level}`;
      const c = s.bags[key];
      const r = num(c?.required);
      if (r > 0) {
        lines.push({
          section: "bags",
          item_key: key,
          colour,
          level,
          qty_on_hand: onHand(c),
          qty_required: r,
        });
      }
    }
  }

  if (s.otherMode === "reimbursement") {
    lines.push({
      section: "other",
      item_key: "reimbursement",
      item_name: `$${REIMBURSEMENT_AMOUNT} reimbursement`,
      amount: REIMBURSEMENT_AMOUNT,
    });
  } else {
    for (const { key, label } of OTHER_ITEMS) {
      const c = s.other[key];
      const r = num(c?.required);
      if (r > 0) {
        lines.push({
          section: "other",
          item_key: key,
          item_name: label,
          qty_on_hand: onHand(c),
          qty_required: r,
        });
      }
    }
  }

  for (const colour of HAT_COLOURS) {
    for (const size of HAT_SIZES) {
      const key = `${colour}|${size}`;
      const c = s.hats[key];
      const r = num(c?.required);
      if (r > 0) {
        lines.push({
          section: "hats",
          item_key: key,
          colour,
          size,
          qty_on_hand: onHand(c),
          qty_required: r,
        });
      }
    }
  }

  for (const size of SOCK_SIZES) {
    const c = s.socks[size];
    const r = num(c?.required);
    if (r > 0) {
      lines.push({
        section: "socks",
        item_key: size,
        size,
        qty_on_hand: onHand(c),
        qty_required: r,
      });
    }
  }

  // Only include sections the service turned on (the top tabs).
  return lines.filter((l) => s.enabledSections[l.section]);
}
